import { z } from 'zod';

// First-level application-support ticketing wire schemas. SEPARATE domain from
// incidents (operational/travel red flags) — no shared fields, no travel
// business data. Two read projections: reporter (own) and management.
//
// `jiraIssueKey` is a MANUAL reference field (the Jira issue an escalated
// ticket was linked to), not a Jira integration: this domain never calls the
// Jira API, stores credentials, or syncs status — unlike apps/incidents.
// `itineraryId`/`itineraryRef` are a reporting reference to the itinerary the
// ticket was raised from; no itinerary business data is read or written.

export const supportTicketTypeValues = [
  'bug',
  'ui_issue',
  'question',
  'support_request',
  'other',
] as const;
export const supportTicketStatusValues = [
  'open',
  'in_progress',
  'resolved',
  'closed',
  'cancelled',
] as const;
export const supportTicketTypeSchema = z.enum(supportTicketTypeValues);
export const supportTicketStatusSchema = z.enum(supportTicketStatusValues);
export type SupportTicketType = z.infer<typeof supportTicketTypeSchema>;
export type SupportTicketStatus = z.infer<typeof supportTicketStatusSchema>;

// { key, url, name, size?, contentType? } — mirrors the uploadthing attachment
// DTO WITHOUT coupling shared -> uploadthing (same approach as incident.ts).
export const supportAttachmentSchema = z.object({
  key: z.string(),
  url: z.string(),
  name: z.string(),
  size: z.number().optional(),
  contentType: z.string().optional(),
});
export type SupportAttachment = z.infer<typeof supportAttachmentSchema>;

// Best-effort client-captured context (route/query/tab/title/browser/entity).
// No `.catchall()` (mirrors incidentPageContextSchema) — unknown keys are
// simply stripped, and every declared field keeps its own precise type
// instead of being forced through a single catchall value type.
export const supportPageContextSchema = z.object({
  pathname: z.string().optional(),
  search: z.string().optional(),
  query: z.record(z.string(), z.string()).optional(),
  module: z.string().optional(),
  route: z.string().optional(),
  tab: z.string().optional(),
  title: z.string().optional(),
  entityType: z.string().optional(),
  entityId: z.string().optional(),
  userAgent: z.string().optional(),
  viewport: z.string().optional(),
});
export type SupportPageContext = z.infer<typeof supportPageContextSchema>;

// A Jira issue key — PROJECT-123 (uppercase project key, digits). Accepted
// case-insensitively at the edge and normalised to uppercase, so a manager can
// type "xt-504" and get "XT-504".
export const JIRA_ISSUE_KEY_PATTERN = /^[A-Z][A-Z0-9_]+-\d+$/;

export const jiraIssueKeySchema = z
  .string()
  .trim()
  .transform((v) => v.toUpperCase())
  .refine((v) => JIRA_ISSUE_KEY_PATTERN.test(v), {
    message: 'Enter a Jira issue key like XT-504.',
  });

// ---- Read projections ----------------------------------------------------
export const ticketOwnSchema = z.object({
  id: z.uuid(),
  // Human-readable reference ("T-100") staff and reporters quote instead of the
  // uuid. Assigned by a DB column DEFAULT, so it is never client-settable and
  // absent from every input schema. Nullable because the column is (see
  // migrations/2026-08-11-support-ticket-ref.sql) — render a fallback.
  // NOT the Jira key: `jiraIssueKey` (XT-504) is the escalated issue, if any.
  ticketRef: z.string().nullable(),
  title: z.string(),
  description: z.string(),
  type: supportTicketTypeSchema,
  status: supportTicketStatusSchema,
  sourceModule: z.string().nullable(),
  applicationUrl: z.string().nullable(),
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  // Itinerary the ticket was raised from (null when raised elsewhere).
  // `itineraryRef` is the create-time snapshot (e.g. PT60154).
  itineraryId: z.uuid().nullable(),
  itineraryRef: z.string().nullable(),
  // Linked Jira issue — key + derived browse URL (null when unlinked, or when
  // the server has no JIRA_BASE_URL configured).
  jiraIssueKey: z.string().nullable(),
  jiraUrl: z.string().nullable(),
  attachments: z.array(supportAttachmentSchema),
  resolutionNotes: z.string().nullable(),
  createdOn: z.string(),
  resolvedOn: z.string().nullable(),
  voteCount: z.number().int().nonnegative(),
  hasVoted: z.boolean(),
});
export type SupportTicketOwn = z.infer<typeof ticketOwnSchema>;

// Lifecycle of the "Post to Jira" action; NULL means never attempted. Local to
// this module — the only consumer is `ticketSchema` below.
const supportJiraPostingStatusSchema = z.enum(['pending', 'posted', 'failed']);

// Jira linkage is MANAGEMENT-ONLY: it is deliberately absent from
// ticketOwnSchema, so a reporter never learns whether (or where) their ticket
// was escalated into an internal tracker.
export const ticketSchema = ticketOwnSchema.extend({
  organizationId: z.uuid(),
  // The owning organization's title, joined for the cross-organization
  // management view (the `system` role's Organization column). Nullable
  // because the join is a LEFT JOIN and an org row may carry no title.
  organizationTitle: z.string().nullable(),
  reporterId: z.uuid(),
  reporterName: z.string().nullable(),
  pageContext: supportPageContextSchema.nullable(),
  modifiedOn: z.string().nullable(),
  resolvedById: z.uuid().nullable(),
  resolvedByName: z.string().nullable(),
  jiraIssueKey: z.string().nullable(),
  jiraUrl: z.string().nullable(),
  jiraPostedOn: z.string().nullable(),
  jiraPostingStatus: supportJiraPostingStatusSchema.nullable(),
  jiraLastError: z.string().nullable(),
});
export type SupportTicket = z.infer<typeof ticketSchema>;

// A conversation entry. `isOwn` is computed server-side instead of shipping
// the author's account id: the reporter surface deliberately never carries
// account UUIDs (see ticketOwnSchema), and "mine vs theirs" is all the UI
// needs to render the thread.
export const supportTicketCommentSchema = z.object({
  id: z.uuid(),
  authorName: z.string().nullable(),
  isOwn: z.boolean(),
  body: z.string(),
  createdOn: z.string(),
});
export type SupportTicketComment = z.infer<typeof supportTicketCommentSchema>;

// Minimal cross-reporter projection (Recent tab + similar list). NEVER exposes
// description/attachments/reporter/pageContext/resolution of others' tickets.
export const similarTicketSchema = z.object({
  id: z.uuid(),
  // Safe to expose across reporters: a reference is an opaque label, carrying
  // none of the description/reporter detail this projection withholds.
  ticketRef: z.string().nullable(),
  title: z.string(),
  type: supportTicketTypeSchema,
  status: supportTicketStatusSchema,
  createdOn: z.string(),
  voteCount: z.number().int().nonnegative(),
  hasVoted: z.boolean(),
});
export type SimilarSupportTicket = z.infer<typeof similarTicketSchema>;

// ---- Inputs --------------------------------------------------------------
export const createTicketInput = z.object({
  title: z.string().min(1).max(200),
  description: z.string().min(1),
  type: supportTicketTypeSchema,
  attachments: z.array(supportAttachmentSchema).default([]),
  sourceModule: z.string().nullish(),
  applicationUrl: z.string().nullish(),
  pageContext: supportPageContextSchema.default({}),
  entityType: z.string().nullish(),
  entityId: z.string().nullish(),
  // Itinerary the reporter was on (Planner or Preview), detected client-side.
  // The server verifies it belongs to the caller's org and snapshots its
  // reference; an unknown/foreign id is silently ignored (the ticket must
  // still be filable — a reporting reference never blocks a bug report).
  itineraryId: z.uuid().nullish(),
});
export type CreateTicketInput = z.infer<typeof createTicketInput>;

// How long after filing a reporter may still edit their own ticket (title,
// description, type, attachments). After that — or as soon as staff move it
// out of `open` — it is locked and further detail goes in the conversation.
export const SUPPORT_OWN_EDIT_WINDOW_MINUTES = 10;

export const updateOwnTicketInput = z.object({
  id: z.uuid(),
  title: z.string().min(1).max(200).optional(),
  description: z.string().min(1).optional(),
  type: supportTicketTypeSchema.optional(),
  attachments: z.array(supportAttachmentSchema).optional(),
});
export type UpdateOwnTicketInput = z.infer<typeof updateOwnTicketInput>;

export const SUPPORT_COMMENT_MAX_LENGTH = 10_000;

export const listTicketCommentsInput = z.object({ ticketId: z.uuid() });
export type ListTicketCommentsInput = z.infer<typeof listTicketCommentsInput>;

// `clientRequestId` is generated by the client per submission attempt and
// reused on retry: replaying the same request returns the comment that already
// exists rather than adding a second one (and a second alert).
export const addTicketCommentInput = z.object({
  ticketId: z.uuid(),
  clientRequestId: z.uuid(),
  body: z.string().trim().min(1).max(SUPPORT_COMMENT_MAX_LENGTH),
});
export type AddTicketCommentInput = z.infer<typeof addTicketCommentInput>;

// `modifiedOn` sorts on last activity — a comment stamps it, so a ticket that
// was just discussed can be brought to the top.
export const supportSortFields = [
  'ticketRef',
  'createdOn',
  'modifiedOn',
  'status',
  'type',
] as const;

export const findMyTicketsInput = z.object({
  status: z.array(supportTicketStatusSchema).optional(),
  page: z.number().int().min(0).default(0),
  pageSize: z.number().int().min(1).max(100).default(20),
  // Last activity first: a ticket someone just commented on is the one the
  // reporter cares about, not the one they filed most recently.
  sortBy: z.enum(supportSortFields).default('modifiedOn'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type FindMyTicketsInput = z.infer<typeof findMyTicketsInput>;

export const recentTicketsInput = z.object({
  limit: z.number().int().min(1).max(50).default(10),
});
export type RecentTicketsInput = z.infer<typeof recentTicketsInput>;

export const searchSimilarTicketsInput = z.object({
  query: z.string(),
  limit: z.number().int().min(1).max(10).default(5),
});
export type SearchSimilarTicketsInput = z.infer<
  typeof searchSimilarTicketsInput
>;

export const toggleVoteInput = z.object({ id: z.uuid() });
export type ToggleVoteInput = z.infer<typeof toggleVoteInput>;

// Resolve the human reference (e.g. PT60154) of the itinerary the reporter is
// currently on, so the create modal can SHOW what it will record. Read-only
// and org-scoped; `itineraryRef` is null for an unknown/foreign/soft-deleted id
// (never an error — failing to label the context must not block filing).
export const resolveItineraryRefInput = z.object({ itineraryId: z.uuid() });
export type ResolveItineraryRefInput = z.infer<typeof resolveItineraryRefInput>;

export const resolveItineraryRefResult = z.object({
  itineraryRef: z.string().nullable(),
});
export type ResolveItineraryRefResult = z.infer<
  typeof resolveItineraryRefResult
>;

export const findTicketsInput = z.object({
  status: z.array(supportTicketStatusSchema).optional(),
  type: z.array(supportTicketTypeSchema).optional(),
  search: z.string().optional(),
  // Narrow a cross-organization listing to ONE organization. Only meaningful
  // for the `system` role, whose listing spans every tenant; for every other
  // caller the router pins the scope to their own org and ignores this field,
  // so it can never be used to read another tenant's tickets.
  organizationId: z.uuid().optional(),
  page: z.number().int().min(0).default(0),
  pageSize: z.number().int().min(1).max(100).default(20),
  // Last activity first — see findMyTicketsInput.
  sortBy: z.enum(supportSortFields).default('modifiedOn'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type FindTicketsInput = z.infer<typeof findTicketsInput>;

export const updateTicketInput = z.object({
  id: z.uuid(),
  status: supportTicketStatusSchema.optional(),
  type: supportTicketTypeSchema.optional(),
  resolutionNotes: z.string().nullish(),
  // Link/unlink the escalated Jira issue. `null` (or an empty string, which
  // the union below folds to null) clears the link; a value must look like a
  // Jira key and is normalised to uppercase. Manual reference only.
  jiraIssueKey: z
    .union([jiraIssueKeySchema, z.literal('').transform(() => null), z.null()])
    .optional(),
});
export type UpdateTicketInput = z.infer<typeof updateTicketInput>;

// ---- Send to Jira --------------------------------------------------------
// Both procedures take the ticket id ONLY. The project and issue type are
// resolved on the server immediately before posting — the values below are
// informational for the confirmation modal, never an input Jira is called with.
export const supportJiraTicketInput = z.object({ id: z.uuid() });

export const supportJiraCreateOptionsSchema = z.object({
  projectKey: z.string(),
  issueType: z.string(),
  /** Set when Jira metadata was incomplete and defaults were assumed. */
  warning: z.string().nullable(),
});
export type SupportJiraCreateOptions = z.infer<
  typeof supportJiraCreateOptionsSchema
>;

// ---- Results -------------------------------------------------------------
export const listMyTicketsResult = z.object({
  data: z.array(ticketOwnSchema),
  count: z.number().int().nonnegative(),
});
export type ListMyTicketsResult = z.infer<typeof listMyTicketsResult>;

export const listTicketsResult = z.object({
  data: z.array(ticketSchema),
  count: z.number().int().nonnegative(),
});
export type ListTicketsResult = z.infer<typeof listTicketsResult>;

export const searchSimilarTicketsResult = z.array(similarTicketSchema);
export const recentTicketsResult = z.array(similarTicketSchema);

// Whole thread, oldest first. No pagination: a helpdesk thread is short, and
// the same call shape as the customer-preview discussion's listMessages.
export const listTicketCommentsResult = z.array(supportTicketCommentSchema);

export const toggleVoteResult = z.object({
  voteCount: z.number().int().nonnegative(),
  hasVoted: z.boolean(),
});
export type ToggleVoteResult = z.infer<typeof toggleVoteResult>;
